import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchCurrentUser,
  updateProfile,
  type CurrentUser,
  type ProfileUpdate,
} from '@wundo/client/auth.ts';

/**
 * Saving one field of your own profile, from any settings tab.
 *
 * Every tab writes the same record through the same endpoint, so the mutation,
 * the cache seeding and the pending/failed flags belong in one place rather
 * than being re-derived per page.
 */
export function useProfileSave() {
  const queryClient = useQueryClient();
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const mutation = useMutation({
    mutationFn: (patch: ProfileUpdate) => updateProfile(user!.id, patch),
    /*
     * Seed the cache from the response rather than invalidating. The server
     * normalises what it stores — a Sunday week turns the weekend back on — so
     * this is also how the page learns what was actually saved.
     */
    onSuccess: (updated: CurrentUser) =>
      queryClient.setQueryData(['currentUser'], updated),
  });

  return {
    user,
    save: mutation.mutate,
    pending: mutation.isPending,
    failed: mutation.isError,
  };
}
